import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { cloneDeep } from 'lodash';

import {
  Dag,
  DagProvider,
  DagreNode,
  DagreResult,
  DbtDagNode,
  getNodeBoundingBox,
  QueryStatus,
  ToolbarItem,
  useDagContext,
} from '@dbt-labs/dbt-dag';
import {
  Button,
  Icon,
  Link,
  RyeconHelp,
  RyeconMinus,
  RyeconPlus,
  Sizes,
} from '@dbt-labs/sourdough';
import { RyeconExpand } from '@dbt-labs/sourdough';

import { ResourcePanelContents } from '@/components/ResourcePanel/ResourcePanelContents';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAllowDbtCloudLinks, useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlag, useFeatureFlags } from '@/context/FeatureFlagProvider';
import { useModalProvider } from '@/context/ModalProvider';
import { usePanel } from '@/context/PanelProvider';
import { useAppliedLineageWithExternalNodes } from '@/hooks/dbtStrategy/useAppliedLineage';
import { useLazyDiscoveryAppliedResourcesWithPublicParents } from '@/hooks/discovery/appliedResources';
import { useResourceWithParentsAndChildren } from '@/hooks/discovery/lineage';
import { useHasNoMetadata, useResourceCounts } from '@/hooks/discovery/resourceCounts';
import { navigateNewTab, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useCurrentSelector } from '@/hooks/useCurrentSelector';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { modelQueryHistorySupported } from '@/util/dbt/adapter';
import { selectDepth, targetResourceSelector } from '@/util/selectorGenerators';
import { Lens } from '../../../../dbt-dag/src/dag/InternalDagInterfaces';
import { ExplorerDag, ExplorerDagNode } from '../ExplorerDag';
import { ProjectPanelContents } from '../ProjectPanel/ProjectPanelContents';
import { XPIndicatorFn } from '../XPIndicator';
import { LineageFloatingNavBar } from './LineageFloatingNavBar';
import { LineagePanelView } from './LineagePanelView';
import { useExpandParam } from './utils';

type LineageParams = {
  fqn?: string | null;
  highlightedUniqueIds?: string[];
  onRenderComplete?: () => void;
  isFullScreen?: boolean;
};

const DEFAULT_EXPAND_ACTION = {
  size: Sizes.xs,
  type: 'secondary' as const,
};

const FULL_LINEAGE_RESOURCE_COUNT_THRESHOLD = 200;

const dagVisResourceTypes = [
  'model',
  'snapshot',
  'seed',
  'source',
  'exposure',
  'metric',
  'semantic_model',
  'saved_query',
  'project',
];
const dagVisResourceSet = new Set(dagVisResourceTypes);

function shouldShowNodeInDAG(
  node: DbtDagNode,
  highlightedUniqueIds: Set<string>,
): boolean {
  return highlightedUniqueIds.has(node.id) || dagVisResourceSet.has(node.resourceType);
}

const useDagErrorMessages = (
  lineageQuery: ReturnType<typeof useAppliedLineageWithExternalNodes>,
) => {
  const hasZeroNodes = lineageQuery.data?.length === 0;
  const { environmentId } = useDbtCloudProject();
  const hasNoData = useHasNoMetadata({ environmentId });
  return useMemo(() => {
    if (lineageQuery.status === 'paused') {
      return [''];
    }
    if (lineageQuery.error) {
      // eslint-disable-next-line no-console
      console.error(
        `There was an error fetching lineage data: ${lineageQuery.error.message}`,
      );
      return [lineageQuery.error.message];
    }
    if (hasZeroNodes) {
      if (hasNoData) {
        return [
          'Please ensure that you have run a job in your production or staging environment in order to see your data in Catalog.',
        ];
      }
      return [`No nodes matched the specified selector.`];
    }
  }, [hasNoData, hasZeroNodes, lineageQuery.error, lineageQuery.status]);
};

export const useShouldRenderFullDag = () => {
  const { environmentId } = useDbtCloudProject();
  const resourceCountsQuery = useResourceCounts({
    environmentId,
  });
  const totalCount = resourceCountsQuery.fetching
    ? FULL_LINEAGE_RESOURCE_COUNT_THRESHOLD
    : resourceCountsQuery.data?.exposure +
      resourceCountsQuery.data?.snapshot +
      resourceCountsQuery.data?.seed +
      resourceCountsQuery.data?.model +
      resourceCountsQuery.data?.source +
      resourceCountsQuery.data?.metric +
      resourceCountsQuery.data?.semantic_model +
      resourceCountsQuery.data?.saved_query;
  return resourceCountsQuery.fetching
    ? undefined
    : totalCount < FULL_LINEAGE_RESOURCE_COUNT_THRESHOLD;
};

const LineageViewWithContext: FC<LineageParams> = memo(
  ({
    fqn,
    highlightedUniqueIds: _highlightedUniqueIds,
    isFullScreen = false,
    onRenderComplete,
  }) => {
    const dagContext = useDagContext();
    useEffect(() => {
      if (dagContext.largeDagWarningShown) {
        datadogRum.addAction('explorer-render-large-dag-warning');
      }
    }, [dagContext.largeDagWarningShown]);

    const lens = dagContext.lens;
    const currentLens = lens || 'default';
    const lensContext = dagContext.lensContext;
    const lensState = lensContext.lensState?.get(currentLens);
    const panel = usePanel();

    const [searchParams, setSearchParams] = useSearchParams();
    const { accountId, hasAccountFeature, hasProjectAccess } = useDbtCloudAccount();
    const {
      projectName,
      projectId,
      accountName,
      environmentId,
      enableModelQueryHistory,
      uniqueId: uniqueIdFromPath,
    } = useDbtCloudProject();

    const [_projectNodesQuery, getProjectNodes] =
      useLazyDiscoveryAppliedResourcesWithPublicParents();

    const [expandedNodes, setExpandedNodes] = useState<ExplorerDagNode[]>([]);
    const [loadingNodes, setLoadingNodes] = useState<number[]>([]);
    const [isDownstreamNodesInitialized, setIsDownstreamNodesInitialized] =
      useState(false);

    const {
      expandedProjectData,
      addExpandedProjectData,
      removeExpandedProjectData,
      setExpandedProjectData,
    } = useExpandParam();

    if (!accountId) throw new Error('Must be in a URL with an account ID');

    const [hasClearedSelection, updateHasClearedSelection] = useState(false);
    const _uniqueIdFromParam = searchParams.get('uniqueId');
    const { uniqueIdParam } = useMemo(() => {
      return {
        uniqueIdParam: _uniqueIdFromParam ? parseUniqueId(_uniqueIdFromParam) : null,
      };
    }, [_uniqueIdFromParam]);
    const environmentIdParam = Number(searchParams.get('environmentId'));
    const selectorParam = isFullScreen
      ? (fqn ?? searchParams.get('select') ?? null)
      : (fqn ?? '');

    const projectIdFromParam = Number(searchParams.get('projectId'));
    const highlightedUniqueIds = useMemo(() => {
      if (_highlightedUniqueIds?.length) {
        return _highlightedUniqueIds;
      }
      const selected = dagContext.selectedNode;

      if (selected) {
        return [selected.id];
      }
      if (hasClearedSelection || !_uniqueIdFromParam) {
        return [];
      }
      return [_uniqueIdFromParam];
    }, [
      _highlightedUniqueIds,
      _uniqueIdFromParam,
      hasClearedSelection,
      dagContext.selectedNode,
    ]);

    const currentSelector = useCurrentSelector(fqn) ?? selectorParam;
    const shouldRenderFullDag = useShouldRenderFullDag();
    const isResourceBasedQueryEnabled = useFeatureFlag(
      'meta4521ExplorerRdpQuery',
      false,
    );
    const featureFlags = useFeatureFlags();

    // This query is heavy and should only be used in the fullScreen
    const fullLineageQuery = useAppliedLineageWithExternalNodes({
      environmentId,
      select: shouldRenderFullDag && !currentSelector ? '*' : currentSelector,
      pause: isResourceBasedQueryEnabled ? !isFullScreen : false,
    });

    // This query is faster and works only for the minified view
    const minifiedLineageQuery = useResourceWithParentsAndChildren(uniqueIdFromPath, {
      pause: isResourceBasedQueryEnabled ? isFullScreen : true,
    });

    const lineageData = isResourceBasedQueryEnabled
      ? !isFullScreen
        ? minifiedLineageQuery
        : fullLineageQuery
      : fullLineageQuery;

    const errorMessages = useDagErrorMessages(lineageData);
    const { data: selectedNodes, status, adapterType } = lineageData;
    const projectScope = useUrlProjectParams();

    // customer has enterprise features but query history is not enabled
    const queryHistoryAvailableNotEnabled =
      hasAccountFeature('explorerQueryHistory') &&
      modelQueryHistorySupported(
        adapterType || '',
        projectScope.environmentType,
        featureFlags,
      ) &&
      !enableModelQueryHistory;

    const lenses: Lens[] = [
      'materialization',
      'status',
      'modelingLayer',
      'testStatus',
      ...(enableModelQueryHistory ? (['usageQueries'] as const) : []),
      ...(queryHistoryAvailableNotEnabled ? (['usageQueriesNudge'] as const) : []),
    ];

    const getTargetEnvironmentType = useTargetEnvironmentType();
    const getTargetUrl = useLinkToExplorer('resourceDetails');
    const getLineageLink = useLinkToExplorer('projectLineage');
    const navigate = useNavigate();
    const enableExternalLinks = useAllowDbtCloudLinks();
    if (isFullScreen) {
      XPIndicatorFn({});
    }

    const { openModal } = useModalProvider();
    const toolbarItems = useMemo((): ToolbarItem[] => {
      if (isFullScreen) return [];
      return [
        {
          label: currentSelector ?? '',
          tooltip: '',
          ryecon: null as any,
          isDisabled: true,
          className: 'max-w-md overflow-auto text-fgDisabled dark:text-fgDecorative',
        },
        {
          ryecon: RyeconExpand,
          label: 'Full Lineage',
          tooltip: 'Enter Full Screen',
          action: () => {
            const targetSelector = selectorParam
              ? targetResourceSelector({
                  fqn: [selectorParam],
                  depth: 3,
                })
              : undefined;

            navigate(
              getLineageLink({
                ...projectScope,
                selector: targetSelector,
                dbtUniqueId: uniqueIdFromPath?.raw,
              }),
            );
          },
        },
      ];
    }, [
      currentSelector,
      getLineageLink,
      isFullScreen,
      navigate,
      projectScope,
      selectorParam,
      uniqueIdFromPath?.raw,
    ]);
    const highlightedSet = useMemo(() => {
      return new Set(highlightedUniqueIds);
    }, [highlightedUniqueIds]);

    const goToResource = (
      targetNode: DbtDagNode,
      selectedNode?: ParsedUniqueId | null,
    ) => {
      const isProject = targetNode?.resourceType === 'project';
      if (isProject) {
        if (!targetNode.environmentId) {
          return;
        }

        if (!targetNode.parents.length) {
          return;
        }

        // Link to the selected node in the downstream project if they are related
        if (selectedNode && targetNode.parents.includes(selectedNode.raw)) {
          const navigateTo = getLineageLink({
            accountId,
            projectId: targetNode.projectId,
            environmentType: getTargetEnvironmentType(targetNode.environmentId!),
            dbtUniqueId: selectedNode.raw,
            environmentId: environmentId,
            originProject: projectId,
          });
          navigateNewTab(navigateTo, navigate, enableExternalLinks);
          return;
        }

        const firstPublicModelParent = selectedNodes?.find(
          (node) => node.id === targetNode.parents[0],
        );

        const navigateToFirst = getLineageLink({
          accountId,
          projectId: targetNode.projectId,
          environmentType: getTargetEnvironmentType(targetNode.environmentId!),
          ...(firstPublicModelParent
            ? {
                environmentId: firstPublicModelParent.environmentId,
                dbtUniqueId: firstPublicModelParent.id,
              }
            : {}),
          originProject: projectId,
        });
        navigateNewTab(navigateToFirst, navigate, enableExternalLinks);
        return;
      }
      // Allow cross-project node navigation
      let targetProjectScope = projectScope;
      if (projectId !== targetNode.projectId) {
        targetProjectScope = {
          accountId,
          projectId: targetNode.projectId,
          environmentType: getTargetEnvironmentType(targetNode.environmentId!),
        };
        const url = getTargetUrl({
          ...targetProjectScope,
          dbtUniqueId: targetNode.id,
          originProject: projectId,
        });
        navigateNewTab(url, navigate, enableExternalLinks);
        return;
      }

      const url = getTargetUrl({
        ...projectScope,
        dbtUniqueId: targetNode.id,
      });
      navigate(url);
    };

    const sidePanelDetails = projectIdFromParam
      ? 'project'
      : uniqueIdParam
        ? 'resource'
        : 'none';

    useEffect(() => {
      if (sidePanelDetails !== 'none') {
        panel.toggleSidePanel(true);
      }
    }, [panel, sidePanelDetails]);

    const isPanelOpen = panel.showSidePanel && sidePanelDetails !== 'none';

    useEffect(() => {
      if (
        searchParams.get('expand') &&
        selectedNodes?.length &&
        !isDownstreamNodesInitialized
      ) {
        initializeExpandedProjectData(
          searchParams.get('expand')?.split(',').map(Number) || [],
        );
      }
      // This should only execute once and on startup
      // eslint-disable-next-line react-compiler/react-compiler
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodes, isDownstreamNodesInitialized]);

    /**
     * Collapse a node and remove their children from the expanded nodes list
     */
    const collapseNode = useCallback(
      (projectId: number, parentId: string) => {
        const projectNode = selectedNodes?.find(
          (node) => node.id === projectId.toString(),
        );

        const visibleParents = projectNode?.parents.filter((parentId) =>
          selectedNodes?.some((node) => node.id === parentId),
        );
        // TODO: check if it's the last open node
        removeExpandedProjectData(projectId, parentId, visibleParents);
        setExpandedNodes((prev) =>
          prev.filter((node) => !node.parents.includes(parentId)),
        );
      },
      [removeExpandedProjectData, selectedNodes],
    );

    /**
     * Expand a node and add their children to the expanded nodes list
     */
    const expandProject = useCallback(
      async (projectId: number, environmentId: number, parents: string[]) => {
        setLoadingNodes((prev) => [...prev, projectId]);
        const res = await getProjectNodes({
          environmentId,
          first: 100,
          publicParents: parents,
        });
        setLoadingNodes((prev) => prev.filter((id) => id !== projectId));
        addExpandedProjectData(projectId, parents);
        setExpandedNodes((prev) => [...prev, ...(res.data ?? [])]);
        return res.data;
      },
      [addExpandedProjectData, getProjectNodes],
    );

    /**
     * Initialize the expanded project data from the url params.
     * This is used to populate the expanded nodes list and the expanded project data.
     */
    const initializeExpandedProjectData = useCallback(
      async (projectIds: number[]) => {
        const expandedProjectsPromises = projectIds.map((projectId) => {
          const projectNode = selectedNodes?.find(
            (node) => node.id === projectId.toString(),
          );
          // this indicated that the project is not in the selected nodes, so we ignore it and remove the param from the url.
          if (!projectNode) {
            removeExpandedProjectData(projectId);
            return null;
          }
          return getProjectNodes({
            environmentId: projectNode.environmentId,
            first: 100,
            publicParents: projectNode.parents,
          });
        });
        const expandedProjectNodes = await Promise.all(
          expandedProjectsPromises.filter(truthy),
        );
        // resolve all nodes and add them to the expanded nodes list
        const expandedProjectsNodesData = expandedProjectNodes
          .map((project) => project.data)
          .flat();
        setExpandedNodes(expandedProjectsNodesData);
        setExpandedProjectData(
          expandedProjectsNodesData.reduce(
            (acc, node) => {
              acc[node.projectId] = Array.from(
                new Set([...(acc[node.projectId] || []), ...node.parents]),
              );
              return acc;
            },
            {} as Record<number, string[]>,
          ),
        );
        setIsDownstreamNodesInitialized(true);
      },
      [
        getProjectNodes,
        removeExpandedProjectData,
        selectedNodes,
        setExpandedProjectData,
      ],
    );

    const hydratedNodes: ExplorerDagNode[] = useMemo(() => {
      if (!selectedNodes) return [];

      const allNodes = [...(selectedNodes ?? [])];
      return allNodes.map((node) => {
        // if the node is a project, we need to add the expand action
        if (node.resourceType === 'project') {
          return {
            ...node,
            access: hasProjectAccess(node.projectId) ? 'public' : 'no_permission',
            expandAction: hasProjectAccess(node.projectId)
              ? {
                  ...DEFAULT_EXPAND_ACTION,
                  testId: 'expand-button',
                  ryecon:
                    expandedProjectData[node.projectId]?.length === node.parents.length
                      ? RyeconMinus
                      : RyeconPlus,
                  isDisabled: loadingNodes.includes(node.projectId),
                  onClick: (event) => {
                    event.stopPropagation();
                    expandProject(node.projectId, node.environmentId, node.parents);
                  },
                }
              : undefined,
          };
          // if the node is a child of an expanded project, we need to add the collapse action
        } else if (
          expandedNodes.some((n) => n.parents.includes(node.id)) &&
          node.projectId === projectId
        ) {
          const childProjectId = expandedNodes.find((n) =>
            n.parents.includes(node.id),
          )?.projectId;
          return {
            ...node,
            expandAction: {
              ...DEFAULT_EXPAND_ACTION,
              testId: 'collapse-button',
              ryecon: RyeconMinus,
              onClick: (event) => {
                event.stopPropagation();
                collapseNode(childProjectId ?? 0, node.id);
              },
            },
          };
        }
        return node;
      });
    }, [
      collapseNode,
      expandProject,
      expandedNodes,
      expandedProjectData,
      hasProjectAccess,
      loadingNodes,
      projectId,
      selectedNodes,
    ]);

    const viewableNodes = (hydratedNodes ?? []).filter((node) =>
      shouldShowNodeInDAG(node, highlightedSet),
    );

    /**
     * This function will get the DAG result and modify the position of the project nodes.
     * It will also add the expanded nodes to the DAG result.
     */
    const onDagLayoutResult = useCallback(
      (result: DagreResult<DbtDagNode>) => {
        const newResult = cloneDeep(result);
        const nonProjectNodes: DagreNode<ExplorerDagNode>[] = newResult.nodes
          .filter((node) => node.resourceType !== 'project')
          .map((node) => {
            return {
              ...node,
              environmentId: node.environmentId ?? 0,
            };
          });
        const maxX = Math.max(
          ...newResult.nodes.map((node) => node.x + node.width).filter(Boolean),
        );
        const minY = Math.min(...newResult.nodes.map((node) => node.y).filter(Boolean));
        const projectNodes: DagreNode<ExplorerDagNode>[] = newResult.nodes
          .filter((node) => node.resourceType === 'project')
          .map((node, index) => {
            const { nodeHeight, nodeWidth } = getNodeBoundingBox(
              node,
              projectName,
              'project',
            );
            return {
              ...node,
              x: maxX + 100,
              // we want to have at most 3 child per project in a row
              y: minY + index * 480,
              width: nodeWidth,
              height: nodeHeight,
              nodeWidth,
              nodeHeight,
              environmentId: node.environmentId ?? 0,
            };
          });

        const expandedNodesWithBoundingBox: DagreNode<ExplorerDagNode>[] =
          expandedNodes.map((node, index) => {
            const xOffset = 360 + Math.floor(index / 3) * 400;
            const yOffset = (index % 3) * 160;

            const projectNode = projectNodes.find(
              (projectNode) => projectNode.id === node.projectId.toString(),
            );
            const { nodeHeight, nodeWidth } = getNodeBoundingBox(
              node,
              projectName,
              'project',
            );

            node.parents.forEach((parentId) => {
              if (
                !newResult.edges.some(
                  (edge) => edge.v === parentId && edge.w === node.id,
                )
              ) {
                newResult.edges.push({
                  v: parentId,
                  w: node.id,
                });
              }
            });

            return {
              ...node,
              x: (projectNode?.x ?? 0) + xOffset,
              y: (projectNode?.y ?? 0) + yOffset,
              width: nodeWidth,
              height: nodeHeight,
              nodeWidth,
              nodeHeight,
              type: 'lens',
            };
          });

        const visibleProjectNodes = projectNodes.filter((node) => {
          const visibleParents = node?.parents.filter((parentId) =>
            nonProjectNodes?.some((node) => node.id === parentId),
          );

          return !visibleParents.every((parentId) =>
            expandedProjectData[node.projectId]?.includes(parentId),
          );
        });

        return {
          ...newResult,
          nodes: [
            ...nonProjectNodes,
            ...visibleProjectNodes,
            ...expandedNodesWithBoundingBox,
          ],
        } as DagreResult<ExplorerDagNode>;
      },
      [expandedNodes, expandedProjectData, projectName],
    );

    return (
      <div className="relative flex h-full w-full">
        <span className="relative h-full w-full flex-1">
          <ExplorerDag
            grain="project"
            nodeType="lens"
            status={
              (errorMessages?.length
                ? 'error'
                : status === 'paused'
                  ? 'error'
                  : status) as QueryStatus
            }
            onDagLayoutResult={onDagLayoutResult}
            disableMagnifiedNode={true}
            resetZoomOnResize={false}
            primaryNodeIds={highlightedUniqueIds}
            activeDbtCloudProject={projectName}
            errorMessages={errorMessages}
            nodes={viewableNodes}
            onRenderComplete={onRenderComplete}
            onComplexityWarn={onRenderComplete}
            padding={isFullScreen ? 0.1 : 0.2}
            minZoom={0.04}
            scrollToZoom={isFullScreen}
            showResetButton={!isFullScreen}
            toolbarItems={toolbarItems}
            onNodeInteraction={({ targetNode, interactionType }) => {
              const isProject = targetNode?.resourceType === 'project';
              if (
                isFullScreen &&
                (interactionType === 'single_click' ||
                  interactionType === 'right_click')
              ) {
                setSearchParams((params) => {
                  panel.toggleSidePanel(true);
                  if (isProject) {
                    /**
                     * Do not delete 'uniqueId', we need it for cross-project linking.
                     *
                     * When a user navigates to a downstream project from a public model,
                     * we want to open the target project DAG with the public model
                     * selected, and show only everything downstream of that model
                     */
                    // params.delete('uniqueId');
                    params.set('projectId', targetNode.projectId.toString());
                    datadogRum.addAction('explorer-single-click-project-node');
                  } else {
                    params.delete('projectId');
                    params.set('uniqueId', targetNode.id);
                  }
                  if (targetNode.environmentId) {
                    params.set('environmentId', targetNode.environmentId.toString());
                  }
                  return params;
                });
              } else if (interactionType === 'double_click') {
                goToResource(targetNode, uniqueIdParam);
              } else if (interactionType === 'clear_selection') {
                updateHasClearedSelection(true);
                setSearchParams((params) => {
                  params.delete('uniqueId');
                  params.delete('projectId');
                  params.delete('environmentId');
                  return params;
                });
              }
            }}
            getContextMenuOptions={(targetResource) => {
              const target = targetResource.fqn;
              if (!target) {
                return [
                  {
                    label: 'View resource details',
                    onSelect: () => {
                      goToResource(targetResource);
                    },
                  },
                ];
              }
              return [
                {
                  label: 'Refocus on node',
                  onSelect: () => {
                    const targetSelector = selectDepth(target, 3, 3);
                    navigate(
                      getLineageLink({
                        ...projectScope,
                        selector: targetSelector,
                        dbtUniqueId: targetResource.id,
                      }),
                    );
                  },
                },
                {
                  label: 'View resource details',
                  onSelect: () => {
                    goToResource(targetResource);
                  },
                },
                {
                  label: 'View upstream',
                  onSelect: () => {
                    const targetSelector = selectDepth(target, '+', 0);
                    navigate(
                      getLineageLink({
                        ...projectScope,
                        selector: targetSelector,
                        dbtUniqueId: targetResource.id,
                      }),
                    );
                  },
                },
                {
                  label: 'View downstream',
                  onSelect: () => {
                    const targetSelector = selectDepth(target, 0, '+');
                    navigate(
                      getLineageLink({
                        ...projectScope,
                        selector: targetSelector,
                        dbtUniqueId: targetResource.id,
                      }),
                    );
                  },
                },
              ];
            }}
          >
            <Dag.ZoomControls />
            {isFullScreen && hasAccountFeature('explorerLineageLenses') && (
              <>
                <Dag.LensLegend
                  onErrorFooterClick={() => {
                    openModal(
                      'Error',
                      'alert',
                      <span data-testid="error-message">
                        {lensState?.description}: {lensState?.descriptionDetails}
                      </span>,
                    );
                  }}
                />
                <Dag.LensSwitcher lenses={lenses} />
              </>
            )}
          </ExplorerDag>

          {status === 'paused' && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 space-y-2 whitespace-nowrap rounded-md border border-borderMuted bg-bgMain p-6 text-center text-sm">
              <div>
                <Icon ryecon={RyeconHelp} size="lg" className="text-fgBrand" />
              </div>
              <div className="text-lg text-fgMain">What are you looking for?</div>
              <div className="text-fgDecorative">
                Search for the lineage you wish to see by
                <br />
                <Link
                  className="inline-block"
                  shouldOpenNewTab
                  isInternal={false}
                  to="https://docs.getdbt.com/reference/node-selection/syntax"
                >
                  using selector syntax
                </Link>{' '}
                or select from the
                <br /> following examples to get started.
              </div>
              <div>
                <SelectorLink
                  selector="*"
                  displayText="Full project lineage"
                  enableLink
                />
                <SelectorLink selector="1+exposure:*" enableLink />
                <SelectorLink selector="access:public+1" enableLink />
              </div>
            </div>
          )}
          {isFullScreen ? (
            <div className="absolute left-0 right-0 top-0">
              <LineageFloatingNavBar
                granularity="project"
                view="lineage"
                accountId={accountId}
                projectId={projectId}
                accountName={accountName}
                projectName={projectName}
              />
            </div>
          ) : null}
        </span>
        {isFullScreen && (
          <span className="flex-0 h-dvh text-left">
            <LineagePanelView
              isOpen={isPanelOpen}
              propsToFreeze={{ uniqueIdParam, projectIdFromParam, sidePanelDetails }}
            >
              {({ projectIdFromParam, uniqueIdParam, sidePanelDetails }) => (
                <>
                  {sidePanelDetails === 'resource' && (
                    <ResourcePanelContents
                      uniqueId={uniqueIdParam}
                      nodeEnvironmentId={environmentIdParam}
                    />
                  )}
                  {sidePanelDetails === 'project' && (
                    <ProjectPanelContents
                      projectId={projectIdFromParam}
                      environmentId={environmentIdParam}
                    />
                  )}
                </>
              )}
            </LineagePanelView>
          </span>
        )}
      </div>
    );
  },
);

const SelectorLink: FC<{
  selector: string;
  displayText?: string;
  enableLink?: boolean;
}> = ({ selector, displayText, enableLink }) => {
  if (!enableLink) {
    return (
      <>
        {displayText && <Button type="secondary" className="mt-6" text={displayText} />}
        {!displayText && (
          <Button type="secondary" className="mt-6" text={selector}></Button>
        )}
      </>
    );
  }
  return (
    <Link isInternal to={`?select=${encodeURIComponent(selector)}`} className="px-1">
      {displayText && <Button type="secondary" className="mt-6" text={displayText} />}
      {!displayText && (
        <Button type="secondary" className="mt-6" text={selector}></Button>
      )}
    </Link>
  );
};

export const LineageView: FC<LineageParams> = (props) => {
  return (
    <DagProvider options={{ disableMagnifiedNode: true }}>
      <LineageViewWithContext {...props} />
    </DagProvider>
  );
};
